import React from 'react';
import { Sparkles } from 'lucide-react';

/**
 * Shared "coming soon" treatment.
 *
 * Two surfaces use it - unlaunched cities on the home page and the Bars tab -
 * so they read as one deliberate decision rather than two separate gaps. Keeps
 * to the site's language: cream surfaces, sage accents, soft shadows,
 * and serif titles.
 */

// The bar guide is not published yet. Flip to false to release it - the
// VenuePage bars tab and its filters already work, so nothing else needs
// changing. Lives here, not in VenuePage, so the home page's city-card stats
// can gate on the same flag instead of quietly showing a live bar count for
// a section that's still a placeholder if you click into it.
export const BARS_COMING_SOON = true;

/** Small glassy pill for overlaying imagery. */
export const ComingSoonBadge: React.FC<{ className?: string; label?: string }> = ({
  className = '',
  label = 'Coming Soon',
}) => (
  // Dark, not translucent-white: these sit over photography that can be pale
  // (a bright sky washed the old glass pill out completely).
  <span
    className={`relative inline-flex items-center gap-1.5 overflow-hidden rounded-full
                border border-white/20 bg-ink-950/70 px-3 py-1.5 text-[11px] font-medium
                uppercase tracking-[0.14em] text-white backdrop-blur-md
                shadow-[0_2px_12px_rgba(0,0,0,0.35)] ${className}`}
  >
    <Sparkles className="w-3 h-3" />
    {label}
    {/* Light sweep - the only motion, so it reads as premium rather than busy. */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 animate-shimmer
                 bg-gradient-to-r from-transparent via-surface/45 to-transparent"
    />
  </span>
);

/** Quieter pill for use on light chrome, e.g. beside a tab label. */
export const ComingSoonTag: React.FC<{ className?: string; label?: string }> = ({
  className = '',
  label = 'Soon',
}) => (
  <span
    className={`inline-flex items-center rounded-full bg-brand-50 px-1.5 py-0.5
                text-[9px] font-semibold uppercase tracking-[0.12em] text-brand-600
                ring-1 ring-inset ring-brand-100 ${className}`}
  >
    {label}
  </span>
);

interface ComingSoonPanelProps {
  icon?: React.ReactNode;
  title: string;
  description: string;
  footnote?: string;
}

/** Full-width placeholder that stands in for a section's content. */
export const ComingSoonPanel: React.FC<ComingSoonPanelProps> = ({
  icon,
  title,
  description,
  footnote,
}) => (
  <div className="relative overflow-hidden rounded-2xl border border-brand-100 bg-surface shadow-sm">
    {/* Faint wash so the panel feels designed rather than empty. */}
    <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-brand-50/70 via-surface to-surface" />
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-y-0 -left-1/3 w-1/3 animate-shimmer
                 bg-gradient-to-r from-transparent via-brand-100/50 to-transparent"
    />

    <div className="relative flex flex-col items-center px-6 py-16 text-center sm:py-20">
      {icon && (
        <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl
                        bg-brand-50 text-brand-500 ring-1 ring-brand-100">
          {icon}
        </div>
      )}

      <span className="mb-3 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-500">
        Coming Soon
      </span>

      <h3 className="font-display font-normal tracking-tight mb-3 text-2xl text-ink-900 sm:text-3xl">
        {title}
      </h3>

      <p className="max-w-md text-sm font-light leading-relaxed text-ink-500">
        {description}
      </p>

      {footnote && (
        <p className="mt-6 text-xs text-ink-400">{footnote}</p>
      )}
    </div>
  </div>
);

export default ComingSoonPanel;
